import { UserAPI } from 'iam/API'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Router } from 'types/definitions'

export default class PasswordResetViewModel {
  /*
   * Reset password request id.
   */
  @observable
  resetPasswordRequestId = ''

  /*
   * Email of the user who requested the password reset.
   */
  @observable
  email = ''

  /*
   * Password to be set for the user.
   */
  @observable
  password = ''

  constructor(
    private userApi: UserAPI,
    private router: Router,
  ) {
    makeObservable(this)
  }

  /*
   * Password contains a lower case letter.
   */
  @computed
  get containsLowerCase() {
    return this.password.toUpperCase() !== this.password
  }

  /*
   * Password contains an upper case letter.
   */
  @computed
  get containsUpperCase() {
    return this.password.toLowerCase() !== this.password
  }

  /*
   * Password contains a number.
   */
  @computed
  get containsOneNumber() {
    return /\d/g.test(this.password)
  }

  /*
   * Password contains special characters.
   */
  @computed
  get containsSpecialCharacters() {
    return /[~`!@#$%^&*+=\\(\\)\-_\\.[\]\\';,/{}|\\":<>\\?]/g.test(
      this.password,
    )
  }

  /*
   * Password minimum length allowed.
   */
  @computed
  get validMinimumLength() {
    return this.password.length >= 8
  }

  /*
   * Password maximum length allowed.
   */
  @computed
  get validMaximumLength() {
    return this.password.length <= 50
  }

  /*
   * Password is valid.
   */
  @computed
  get valid() {
    return (
      this.containsLowerCase &&
      this.containsUpperCase &&
      this.containsOneNumber &&
      this.containsSpecialCharacters &&
      this.validMinimumLength &&
      this.validMaximumLength
    )
  }

  /*
   * Activates the view model.
   * @param resetPasswordRequestId Reset password request id.
   * @param email Email of the user who requested the password reset.
   */
  activate(resetPasswordRequestId: string, email: string) {
    this.resetPasswordRequestId = resetPasswordRequestId
    this.email = email
  }

  /*
   * Resets the password.
   */
  resetPassword = task.resolved(async () => {
    if (!this.valid) {
      throw new Error('Invalid password')
    }

    return this.userApi
      .resetPassword(this.resetPasswordRequestId, this.password)
      .then(this.gotToHome)
  })

  /*
   * Redirects to home page.
   */
  @action.bound
  private gotToHome() {
    this.router.replace('/')
  }
}
